<template>
  <b-modal
    v-model="showModal"
    title="Organizar Ficha Automaticamente"
    size="lg"
    centered
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <div class="auto-organize-form">
      <!-- Form Selection (multi) -->
      <div class="form-group">
        <label>Formulários <span class="text-danger">*</span></label>
        <div v-if="loadingForms" class="text-muted" style="font-size: 13px;">
          Carregando formulários...
        </div>
        <div v-else-if="forms.length === 0" class="text-muted" style="font-size: 13px;">
          Nenhum formulário disponível.
        </div>
        <div v-else class="forms-checklist">
          <label v-for="form in forms" :key="form.id" class="form-check-item">
            <input
              type="checkbox"
              :value="form.id"
              v-model="selectedFormIds"
              :disabled="loading"
            />
            <span class="form-check-label">
              {{ form.name }}
              <small class="form-type-badge" :class="'form-type-' + form.type">
                {{ formTypeLabel(form.type) }}
              </small>
            </span>
          </label>
        </div>
        <small class="text-muted" v-if="forms.length > 0">
          Selecione os formulários que devem alimentar a ficha. O de inscrição vira a base e os de retorno entram como abas adicionais.
        </small>
      </div>

      <!-- Options -->
      <div class="form-group">
        <label>Opções</label>
        <div class="options-list">
          <label class="option-item">
            <input
              type="checkbox"
              v-model="includeStandardFields"
              :disabled="loading"
            />
            <span>Incluir campos padrão (Nome, Email, CPF, Telefone...)</span>
          </label>
          <label class="option-item">
            <input
              type="checkbox"
              v-model="useFormLayout"
              :disabled="loading"
            />
            <span>Usar layout do formulário (larguras dos campos)</span>
          </label>
        </div>
      </div>

      <!-- Warning -->
      <div v-if="tabCount > 0 || fieldCount > 0" class="warning-box">
        <i class="bx bx-error-circle"></i>
        <span>
          A ficha atual possui <strong>{{ tabCount }} aba(s)</strong> e
          <strong>{{ fieldCount }} campo(s)</strong>. Serão substituídos.
        </span>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-box">
        <div class="spinner-border spinner-border-sm text-primary mr-2"></div>
        <span>Organizando ficha...</span>
      </div>
    </div>

    <template #modal-footer>
      <button class="btn btn-secondary" @click="cancel" :disabled="loading">
        Cancelar
      </button>
      <button
        class="btn btn-primary"
        @click="organize"
        :disabled="!selectedFormIds.length || loading"
      >
        <i class="bx" :class="loading ? 'bx-loader-alt bx-spin' : 'bx-magic-wand'"></i>
        {{ loading ? 'Organizando...' : 'Organizar' }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import http from '@/http'

export default {
  name: 'AutoOrganizeModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    tabs: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: function () {
    return {
      forms: [],
      selectedFormIds: [],
      includeStandardFields: true,
      useFormLayout: true,
      loading: false,
      loadingForms: false
    }
  },
  computed: {
    showModal: {
      get: function () { return this.visible },
      set: function (val) { if (!val) this.$emit('close') }
    },
    tabCount: function () {
      return this.tabs ? this.tabs.length : 0
    },
    fieldCount: function () {
      if (!this.tabs) return 0
      var count = 0
      this.tabs.forEach(function (tab) {
        if (tab.fields) count += tab.fields.length
      })
      return count
    }
  },
  watch: {
    visible: function (val) {
      if (val && this.forms.length === 0) {
        this.loadForms()
      }
    }
  },
  methods: {
    loadForms: function () {
      var self = this
      self.loadingForms = true
      http.get('/admin/forms', { params: { all: true } })
        .then(function (response) {
          var data = response.data.data || response.data || []
          // Só formulários de inscrição e ações de retorno (exclui checkin_lead e afins)
          var allowed = ['registration', 'action']
          self.forms = data.filter(function (f) {
            if (!f.status) return false
            var type = f.type || 'registration'
            return allowed.indexOf(type) !== -1
          })
          // Pré-seleciona todos os disponíveis — admin desmarca se quiser
          self.selectedFormIds = self.forms.map(function (f) { return f.id })
        })
        .catch(function (error) {
          console.error('Error loading forms:', error)
        })
        .then(function () {
          self.loadingForms = false
        })
    },

    formTypeLabel: function (type) {
      if (type === 'registration') return 'Inscrição'
      if (type === 'action') return 'Retorno'
      return type || 'Outro'
    },

    organize: function () {
      if (!this.selectedFormIds.length) return
      this.$emit('organize', {
        form_ids: this.selectedFormIds,
        include_standard_fields: this.includeStandardFields,
        use_form_layout: this.useFormLayout
      })
    },

    setLoading: function (val) {
      this.loading = val
    },

    cancel: function () {
      if (!this.loading) {
        this.$emit('close')
      }
    },

    onHidden: function () {
      if (!this.loading) {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.auto-organize-form .form-group {
  margin-bottom: 20px;
}

.auto-organize-form label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

/* Forms checklist */
.forms-checklist {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
  background: #fafbfc;
}

.form-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 400;
  margin: 0;
  padding: 4px 0;
}

.form-check-item input[type="checkbox"] {
  flex-shrink: 0;
}

.form-check-label {
  font-size: 13px;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.form-type-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 4px;
}
.form-type-badge.form-type-registration {
  background: #dbeafe;
  color: #1e40af;
}
.form-type-badge.form-type-action {
  background: #fef3c7;
  color: #92400e;
}

/* Options List */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 400;
  margin-bottom: 0;
}

.option-item input[type="checkbox"] {
  flex-shrink: 0;
}

.option-item span {
  font-size: 13px;
  color: #495057;
}

/* Warning Box */
.warning-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  font-size: 13px;
  color: #856404;
}

.warning-box i {
  font-size: 18px;
  flex-shrink: 0;
}

/* Loading Box */
.loading-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #e8f4fd;
  border: 1px solid #bee5eb;
  border-radius: 6px;
  font-size: 13px;
  color: #0c5460;
  margin-top: 12px;
}
</style>
